/**
 * /docs — audience-gated documentation portal.
 *
 * Authenticated-only by `<ProtectedRoute>`. Sidebar lists every doc
 * the viewer is allowed to see (per backend audience filtering); main
 * panel renders the selected doc's markdown with a watermark in the
 * top-right carrying the viewer's email + classification.
 *
 * No anonymous access — per IP-owner mandate (iter116). Confidential
 * leaks are a legal nightmare; default-closed.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Lock, FileText, ShieldCheck, Loader2, AlertOctagon } from "lucide-react";
import DocsAdminPanel from "@/components/DocsAdminPanel";

const CLASSIFICATION_VISUAL = {
  general: { label: "General", color: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  confidential: { label: "Confidential", color: "text-amber-700", bg: "bg-amber-50", ring: "ring-amber-200" },
  secret: { label: "Secret", color: "text-rose-700", bg: "bg-rose-50", ring: "ring-rose-200" },
  nda: { label: "NDA", color: "text-rose-800", bg: "bg-rose-100", ring: "ring-rose-300" },
};

function ClassificationPill({ classification }) {
  const v = CLASSIFICATION_VISUAL[classification] || CLASSIFICATION_VISUAL.confidential;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${v.bg} ${v.color} ${v.ring}`}>
      <Lock size={10} /> {v.label}
    </span>
  );
}

export default function Docs() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [index, setIndex] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pull the index once on mount.
  useEffect(() => {
    setLoading(true);
    api.get("/docs/index")
      .then(({ data }) => setIndex(data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load docs index"))
      .finally(() => setLoading(false));
  }, []);

  // Pull the active doc whenever the slug changes (or default to the first).
  useEffect(() => {
    if (!index?.items?.length) return;
    const target = slug || index.items[0].slug;
    if (!slug) {
      navigate(`/docs/${target}`, { replace: true });
      return;
    }
    setDocLoading(true);
    setError(null);
    api.get(`/docs/page/${target}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => {
        setDoc(null);
        setError(e?.response?.status === 404
          ? "This document doesn't exist, or you don't have access."
          : (e?.response?.data?.detail || "Failed to load document"));
      })
      .finally(() => setDocLoading(false));
  }, [slug, index, navigate]);

  const viewer = index?.viewer;

  // Watermark text — the viewer's email + classification + a short
  // viewed-at timestamp. The classification colour matches the pill so
  // the visual hierarchy is consistent.
  const watermark = useMemo(() => {
    if (!doc || !viewer) return null;
    const cl = doc.classification || "confidential";
    const vis = CLASSIFICATION_VISUAL[cl] || CLASSIFICATION_VISUAL.confidential;
    const time = (doc.viewer?.viewed_at || new Date().toISOString()).slice(0, 16).replace("T", " ");
    return { text: `${viewer.email} · ${vis.label.toUpperCase()} · ${time} UTC`, color: vis.color };
  }, [doc, viewer]);

  return (
    <div className="min-h-screen bg-ek-canvas" data-testid="docs-portal">
      <Seo title="ERIO · Docs" description="ERIO internal documentation portal" />
      <Navbar />

      <main className="mx-auto flex max-w-7xl gap-8 px-6 py-8">
        {/* Sidebar */}
        <aside className="w-72 flex-shrink-0">
          <div className="sticky top-6 rounded-2xl border border-black/10 bg-white p-4">
            <div className="mb-3 flex items-center gap-2 border-b border-black/5 pb-3">
              <ShieldCheck size={16} className="text-ek-purple" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ek-ink">Documentation</h2>
            </div>
            {loading ? (
              <p className="text-xs text-ek-ink/55">Loading…</p>
            ) : error && !index ? (
              <p className="text-xs text-rose-600">{error}</p>
            ) : index?.items?.length === 0 ? (
              <p className="text-xs text-ek-ink/55">
                No docs available for your account. If you need access, email{" "}
                <a className="text-ek-purple hover:underline" href="mailto:support@ekselens.com">support@ekselens.com</a>.
              </p>
            ) : (
              <nav className="space-y-1" data-testid="docs-sidebar">
                {index?.items?.map((d) => {
                  const active = d.slug === slug;
                  return (
                    <Link
                      key={d.slug}
                      to={`/docs/${d.slug}`}
                      data-testid={`docs-sidebar-item-${d.slug}`}
                      className={`flex items-start gap-2 rounded-md px-2.5 py-2 text-xs transition-colors ${
                        active
                          ? "bg-ek-purple/10 text-ek-purple"
                          : "text-ek-ink/75 hover:bg-ek-canvas hover:text-ek-ink"
                      }`}
                    >
                      <FileText size={12} className="mt-0.5 flex-shrink-0" />
                      <span className="flex-1">
                        <span className="block font-medium leading-tight">{d.title}</span>
                        <span className="mt-1 block">
                          <ClassificationPill classification={d.classification} />
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </nav>
            )}
            {viewer && (
              <div className="mt-5 border-t border-black/5 pt-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">Your access</p>
                <p className="mt-1 truncate text-xs text-ek-ink/70" data-testid="docs-viewer-email">{viewer.email}</p>
                <p className="mt-1 flex flex-wrap gap-1">
                  {viewer.audiences.map((a) => {
                    const exp = viewer.audience_expiry?.[a];
                    const expiringSoon = exp && exp.hours_remaining <= 72;
                    return (
                      <span
                        key={a}
                        className={`rounded-full px-1.5 py-0.5 text-[9px] ${
                          expiringSoon
                            ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                            : "bg-ek-canvas text-ek-ink/65"
                        }`}
                        data-testid={`docs-audience-${a}`}
                        title={exp ? `Expires ${exp.expires_at}` : undefined}
                      >
                        {a}
                        {exp && (
                          <span className="ml-1 font-semibold" data-testid={`docs-audience-expiry-${a}`}>
                            · {exp.hours_remaining < 24
                              ? `${exp.hours_remaining}h`
                              : `${Math.floor(exp.hours_remaining / 24)}d`} left
                          </span>
                        )}
                      </span>
                    );
                  })}
                </p>
              </div>
            )}
            {viewer?.audiences?.includes("ip-owner") && (
              <DocsAdminPanel
                onChanged={() => {
                  // Pull the index back so the user's own audience pills
                  // and expiry badges stay in lock-step after the IP-owner
                  // grants something to themselves (rare but possible).
                  api.get("/docs/index").then(({ data }) => setIndex(data));
                }}
              />
            )}
          </div>
        </aside>

        {/* Content */}
        <article className="relative min-w-0 flex-1">
          {watermark && (
            <div
              data-testid="docs-watermark"
              aria-hidden="true"
              className={`pointer-events-none absolute right-4 top-4 select-none text-[10px] font-mono opacity-50 ${watermark.color}`}
            >
              {watermark.text}
            </div>
          )}

          {docLoading ? (
            <div className="flex items-center gap-2 rounded-2xl border border-black/10 bg-white p-12 text-sm text-ek-ink/55">
              <Loader2 className="animate-spin" size={14} /> Loading document…
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8" data-testid="docs-error">
              <AlertOctagon className="text-rose-700" size={22} />
              <h2 className="mt-2 text-lg font-semibold text-rose-800">Unable to load this document</h2>
              <p className="mt-1 text-sm text-rose-700">{error}</p>
              <p className="mt-3 text-xs text-rose-700/80">
                Every access (including denied ones) is audit-logged. If you believe this is an error, contact{" "}
                <a className="underline" href="mailto:support@ekselens.com">support@ekselens.com</a>.
              </p>
            </div>
          ) : doc ? (
            <div className="rounded-2xl border border-black/10 bg-white p-8 shadow-sm" data-testid="docs-content">
              <header className="mb-6 border-b border-black/5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <h1 className="text-3xl font-semibold tracking-tight text-ek-ink" data-testid="docs-title">{doc.title}</h1>
                  <ClassificationPill classification={doc.classification} />
                </div>
                <p className="mt-2 text-[11px] text-ek-ink/55">
                  Last reviewed {doc.last_reviewed || "—"} · Owner {doc.owner || "—"}{" "}
                  {doc.audiences?.length > 0 && (
                    <> · Visible to {doc.audiences.join(", ")}</>
                  )}
                </p>
              </header>
              <div className="markdown-body prose prose-sm max-w-none text-ek-ink/85">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({ node, ...props }) => (
                      <div className="my-4 overflow-x-auto">
                        <table className="w-full text-xs" {...props} />
                      </div>
                    ),
                    th: ({ node, ...props }) => (
                      <th className="border-b border-black/10 bg-ek-canvas px-3 py-2 text-left font-semibold" {...props} />
                    ),
                    td: ({ node, ...props }) => (
                      <td className="border-b border-black/5 px-3 py-2 align-top" {...props} />
                    ),
                    code: ({ node, inline, ...props }) =>
                      inline
                        ? <code className="rounded bg-ek-canvas px-1.5 py-0.5 font-mono text-[12px]" {...props} />
                        : <code className="block whitespace-pre overflow-x-auto rounded bg-ek-ink/[0.04] p-3 font-mono text-[12px]" {...props} />,
                    h1: ({ node, ...props }) => <h2 className="mt-6 mb-3 text-2xl font-semibold text-ek-ink" {...props} />,
                    h2: ({ node, ...props }) => <h3 className="mt-5 mb-2 text-lg font-semibold text-ek-ink" {...props} />,
                    h3: ({ node, ...props }) => <h4 className="mt-4 mb-2 text-sm font-semibold text-ek-ink" {...props} />,
                    a: ({ node, ...props }) => <a className="text-ek-purple hover:underline" {...props} />,
                  }}
                >
                  {doc.body}
                </ReactMarkdown>
              </div>
              <footer className="mt-8 border-t border-black/5 pt-3 text-center text-[10px] text-ek-ink/45">
                {doc.classification === "confidential" && "Confidential — covered by your MSA / partner agreement."}
                {doc.classification === "secret" && "Secret — Ekselens internal only."}
                {doc.classification === "general" && "© 2026 Ekselens Consulting Ltd — Confidential to ERIO licensees."}
                {doc.classification === "nda" && `NDA-gated — disclosure restricted to ${viewer?.email || "(viewer)"}.`}
                <br />
                Access logged · viewer {viewer?.email} · {new Date().toISOString().slice(0, 16).replace("T", " ")} UTC
              </footer>
            </div>
          ) : (
            <p className="text-sm text-ek-ink/55">Select a document from the sidebar.</p>
          )}
        </article>
      </main>
    </div>
  );
}
